import { useCallback, useEffect, useMemo, useState } from 'react';
import type { PinMessage } from '@fdh/shared';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  AutoTranslationHint,
  resolveTranslatedDisplayText,
  translationLanguageNameKey,
} from '../translation';
import { AppModal } from '../ui/AppModal';
import { pinMessageIconSvg, pinMessageIconTheme } from './pinMessageIconTheme';

type PinMessageTickerProps = {
  items: PinMessage[];
  rotationSeconds?: number;
  canManage?: boolean;
  initialPinMessageId?: string;
  onInitialPinMessageOpened?: () => void;
  onEditCurrent?: (item: PinMessage) => void;
  onRemoveCurrent?: (item: PinMessage) => void;
  /** Helper home: show translation controls when employer language differs. */
  enableHelperTranslation?: boolean;
  showOriginalGlobal?: boolean;
  onToggleShowOriginalGlobal?: () => void;
};

const PencilIcon = () => (
  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
    />
  </svg>
);

const TrashIcon = () => (
  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
    />
  </svg>
);

export const PinMessageTicker = ({
  items,
  rotationSeconds = 5,
  canManage = false,
  initialPinMessageId = '',
  onInitialPinMessageOpened,
  onEditCurrent,
  onRemoveCurrent,
  enableHelperTranslation = false,
  showOriginalGlobal = false,
  onToggleShowOriginalGlobal,
}: PinMessageTickerProps) => {
  const { t, language } = useLanguage();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [initialPinHandled, setInitialPinHandled] = useState(false);
  const [showOriginalByMessageId, setShowOriginalByMessageId] = useState<Record<string, boolean>>({});

  useEffect(() => {
    setShowOriginalByMessageId({});
  }, [language]);

  useEffect(() => {
    if (items.length <= 1 || modalOpen) {
      return;
    }
    const timer = window.setTimeout(() => {
      setCurrentIndex((prev) => (prev + 1) % items.length);
    }, rotationSeconds * 1000);
    return () => window.clearTimeout(timer);
  }, [currentIndex, items.length, modalOpen, rotationSeconds]);

  useEffect(() => {
    if (items.length === 0) {
      return;
    }
    if (currentIndex < items.length) {
      return;
    }
    setCurrentIndex(0);
  }, [currentIndex, items.length]);

  useEffect(() => {
    if (initialPinHandled || initialPinMessageId.trim() === '' || items.length === 0) {
      return;
    }
    const targetIndex = items.findIndex((item) => item.id === initialPinMessageId);
    if (targetIndex < 0) {
      return;
    }
    setCurrentIndex(targetIndex);
    setModalOpen(true);
    setInitialPinHandled(true);
    onInitialPinMessageOpened?.();
  }, [initialPinHandled, initialPinMessageId, items, onInitialPinMessageOpened]);

  const current = useMemo(() => items[currentIndex], [currentIndex, items]);

  const firstTranslationMeta = useMemo(
    () => items.find((item) => item.translation?.hasTranslation)?.translation,
    [items],
  );

  const hasAnyTranslation = Boolean(firstTranslationMeta);
  const showTranslationControls = enableHelperTranslation && hasAnyTranslation;

  const targetLanguageLabel = firstTranslationMeta
    ? t(translationLanguageNameKey(firstTranslationMeta.targetLanguage))
    : '';

  const handleToggleMessageOriginal = useCallback((messageId: string): void => {
    setShowOriginalByMessageId((prev) => ({
      ...prev,
      [messageId]: !(prev[messageId] ?? false),
    }));
  }, []);

  if (!current) {
    return null;
  }

  const theme = pinMessageIconTheme(current.iconType);
  const handlePrev = () => {
    setCurrentIndex((prev) => (prev - 1 + items.length) % items.length);
  };
  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % items.length);
  };

  const tickerTitle = resolveTranslatedDisplayText(
    current.title,
    current.translation?.fields.title,
    showOriginalGlobal,
  );

  const showOriginalInModal = showOriginalByMessageId[current.id] ?? false;
  const modalTitle = resolveTranslatedDisplayText(
    current.title,
    current.translation?.fields.title,
    showOriginalInModal,
  );
  const modalContent = resolveTranslatedDisplayText(
    current.content,
    current.translation?.fields.content,
    showOriginalInModal,
  );

  const currentHasTranslation = Boolean(current.translation?.hasTranslation);
  const currentTargetLanguageLabel = current.translation?.targetLanguage
    ? t(translationLanguageNameKey(current.translation.targetLanguage))
    : targetLanguageLabel;

  return (
    <>
      <section className={`mt-2 rounded-xl border bg-white/90 p-2 shadow-sm dark:bg-gray-900/70 ${theme.border}`}>
        <div className="mb-1 flex items-center justify-between px-1">
          <div className="flex min-w-0 items-center gap-1 text-xs font-semibold text-gray-600 dark:text-gray-300">
            <svg className="h-3.5 w-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
              <g transform="translate(24 0) scale(-1 1) rotate(-40 12 12)">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 3h6l1 4h-2v5l2 2v1H8v-1l2-2V7H8l1-4z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 15v6"
                />
              </g>
            </svg>
            <span className="truncate">{t('pinMessage.ticker.title')}</span>
            {showTranslationControls ? (
              <AutoTranslationHint
                targetLanguageLabel={targetLanguageLabel}
                showingOriginal={showOriginalGlobal}
                onToggleOriginal={() => onToggleShowOriginalGlobal?.()}
              />
            ) : null}
          </div>
          <span className="shrink-0 text-xs font-semibold text-gray-500 dark:text-gray-400">
            {t('pinMessage.ticker.count', { current: currentIndex + 1, total: items.length })}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className={`inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${theme.badge}`}>
            {pinMessageIconSvg(current.iconType)}
          </span>
          <button
            type="button"
            onClick={() => setModalOpen(true)}
            className={`min-w-0 flex-1 truncate text-left text-sm font-semibold ${theme.text} focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500`}
            aria-label={t('pinMessage.ticker.openDetails')}
          >
            {tickerTitle}
          </button>
          {items.length > 1 ? (
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={handlePrev}
                className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-800"
                aria-label={t('pinMessage.ticker.prev')}
              >
                <span aria-hidden>{'<'}</span>
              </button>
              <button
                type="button"
                onClick={handleNext}
                className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-800"
                aria-label={t('pinMessage.ticker.next')}
              >
                <span aria-hidden>{'>'}</span>
              </button>
            </div>
          ) : null}
        </div>
      </section>

      {modalOpen ? (
        <AppModal
          open
          onClose={() => setModalOpen(false)}
          closeAriaLabel={t('pinMessage.action.close')}
          ariaLabel={modalTitle}
          backdropZClass="z-50"
        >
          <div className="mb-2 flex items-center gap-1.5 pr-10 text-xs font-semibold text-gray-500 dark:text-gray-400">
            <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
              <g transform="translate(24 0) scale(-1 1) rotate(-40 12 12)">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 3h6l1 4h-2v5l2 2v1H8v-1l2-2V7H8l1-4z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 15v6"
                />
              </g>
            </svg>
            <span>{t('pinMessage.ticker.title')}</span>
          </div>
          <div className="flex items-start gap-2">
            <span className={`mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${theme.badge}`}>
              {pinMessageIconSvg(current.iconType)}
            </span>
            <div className="flex min-w-0 flex-1 items-start gap-1">
              <h3 id="pin-message-modal-title" className="min-w-0 flex-1 text-lg font-semibold text-gray-900 dark:text-gray-100">
                {modalTitle}
              </h3>
              {enableHelperTranslation && currentHasTranslation ? (
                <AutoTranslationHint
                  targetLanguageLabel={currentTargetLanguageLabel}
                  showingOriginal={showOriginalInModal}
                  onToggleOriginal={() => handleToggleMessageOriginal(current.id)}
                  className="mt-0.5"
                />
              ) : null}
            </div>
          </div>
          <p className="mt-3 whitespace-pre-wrap break-words text-sm text-gray-700 dark:text-gray-200">
            {modalContent}
          </p>
          <div className="mt-4 flex items-start justify-between gap-3 border-t border-gray-200 pt-3 dark:border-gray-700">
            <div className="space-y-1 text-xs text-gray-500 dark:text-gray-400">
              <p>{t('pinMessage.detail.createdAt', { value: current.createdAt })}</p>
              {current.updatedAt !== current.createdAt ? (
                <p>{t('pinMessage.detail.updatedAt', { value: current.updatedAt })}</p>
              ) : null}
            </div>
            {canManage ? (
              <div className="flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => onEditCurrent?.(current)}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-800 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-400 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700"
                  aria-label={t('pinMessage.action.edit')}
                  tabIndex={0}
                >
                  <PencilIcon />
                </button>
                <button
                  type="button"
                  onClick={() => onRemoveCurrent?.(current)}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-rose-200 bg-rose-50 text-rose-800 hover:bg-rose-100 focus:outline-none focus:ring-2 focus:ring-rose-400 dark:border-rose-900/60 dark:bg-rose-950/50 dark:text-rose-100 dark:hover:bg-rose-950/80"
                  aria-label={t('pinMessage.action.remove')}
                  tabIndex={0}
                >
                  <TrashIcon />
                </button>
              </div>
            ) : null}
          </div>
        </AppModal>
      ) : null}
    </>
  );
};
